import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { EmailLog } from '../types';
import apiService from '../services/api';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { ColDef } from 'ag-grid-community';
import {
  PageContainerWide,
  PageHeader,
  Card,
  CardBody,
  Button,
  Input,
  Select,
  Alert,
  Badge,
  Modal,
  StatCard,
  StatGrid
} from '../components/ui';
import './StoricoEmailPage.css';

const Controls = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled(Input)`
  width: 250px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DetailRow = styled.div`
  margin-bottom: var(--space-md);
`;

const StoricoEmailPage: React.FC = () => {
  const { user } = useAuth();
  const [emails, setEmails] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    inviata: 0,
    errore: 0,
    in_coda: 0
  });
  const [alert, setAlert] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  // Stati per modale dettagli
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

// Column definitions per AG Grid
  const columnDefs: ColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      width: 80,
      pinned: 'left'
    },
    {
      headerName: 'Data',
      field: 'created_at',  // Aggiornato per corrispondere al DB
      width: 160,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString('it-IT');
      }
    },
    {
      headerName: 'Destinatario',
      field: 'destinatario',  // Aggiornato per corrispondere al DB
      minWidth: 200,
      flex: 1
    },
    {
      headerName: 'Oggetto',
      field: 'oggetto',
      minWidth: 300,
      flex: 2,
      cellRenderer: (params: any) => {
        if (!params.value) return '';
        const oggetto = params.value;
        const shortText = oggetto.length > 50 ? oggetto.substring(0, 50) + '...' : oggetto;
        return React.createElement('span', { title: oggetto }, shortText);
      }
    },
    {
      headerName: 'Stato',
      field: 'stato',
      width: 120,
      cellRenderer: (params: any) => {
        const stato = params.value;
        let className = 'stato-badge ';
        switch (stato) {
          case 'inviata':
            className += 'stato-inviata';
            break;
          case 'in coda':
            className += 'stato-in-coda';
            break;
          case 'errore':
          case 'fallita':
            className += 'stato-errore';
            break;
          default:
            className += 'stato-default';
        }
        return React.createElement('span', { className }, stato);
      }
    },
    {
      headerName: 'Data Invio',
      field: 'sent_at',
      width: 160,
      valueFormatter: (params) => {
        if (!params.value) return 'Non inviata';
        return new Date(params.value).toLocaleString('it-IT');
      }
    },
    {
      headerName: 'Errore',
      field: 'errore',
      minWidth: 200,
      flex: 1,
      cellRenderer: (params: any) => {
        if (!params.value) return '';
        const errore = params.value;
        const shortText = errore.length > 50 ? errore.substring(0, 50) + '...' : errore;
        return React.createElement('span', {
          title: errore,
          style: { color: 'var(--error)', fontSize: '0.9em' }
        }, shortText);
      }
    },
    {
      headerName: 'Azioni',
      field: 'actions',
      width: 100,
      pinned: 'right',
      cellRenderer: (params: any) => {
        return React.createElement('button', {
          className: 'btn-dettagli',
          onClick: () => handleViewDetails(params.data),
          title: 'Visualizza dettagli email'
        }, 'Dettagli');
      }
    }
  ];

  const caricaEmails = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Caricamento email log...');
      const data = await apiService.getEmailLog({ limit: 1000 });
      console.log('Dati ricevuti dall\'API:', data);

      // Il backend restituisce { emails: [], total: ..., limit: ..., offset: ... }
      const emailArray = Array.isArray(data?.emails) ? data.emails : [];
      console.log('Email array estratto:', emailArray);
      console.log('Numero di email:', emailArray.length);

      setEmails(emailArray);
      calcolaStatistiche(emailArray);

      if (emailArray.length === 0) {
        setAlert({ show: true, message: 'Nessuna email trovata nel sistema', type: 'error' });
      }
    } catch (error) {
      console.error('Errore caricamento email log:', error);
      const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
      setAlert({ show: true, message: `Errore nel caricamento dello storico email: ${errorMessage}`, type: 'error' });
      // In caso di errore, imposta un array vuoto
      setEmails([]);
      calcolaStatistiche([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Verifica se l'utente è admin
    if (user && user.ruolo !== 'admin') {
      setAlert({ show: true, message: "Non hai i permessi necessari per accedere a questa pagina.", type: 'error' });
      setLoading(false);
      return;
    }

    caricaEmails();
  }, [caricaEmails, user]);

  const calcolaStatistiche = (data: EmailLog[]) => {
    // Assicurati che data sia un array
    const emailArray = Array.isArray(data) ? data : [];

    const stats = {
      total: emailArray.length,
      inviata: emailArray.filter(email => email.stato === 'inviata').length,
      errore: emailArray.filter(email => email.stato === 'errore' || email.stato === 'fallita').length,
      in_coda: emailArray.filter(email => email.stato === 'in coda').length  // Aggiornato stato
    };

    setStats(stats);
  };

  // Funzioni per la modale dettagli
  const handleViewDetails = (email: EmailLog) => {
    setSelectedEmail(email);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setSelectedEmail(null);
    setShowDetailsModal(false);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/D';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const filteredEmails = Array.isArray(emails) ? emails.filter(email => {
    const matchesSearch = searchTerm === '' ||
      email.destinatario?.toLowerCase().includes(searchTerm.toLowerCase()) ||  // Aggiornato campo
      email.oggetto?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === '' || email.stato === statusFilter;

    return matchesSearch && matchesStatus;
  }) : [];

  if (user && user.ruolo !== 'admin') {
    return (
      <PageContainerWide>
        <Alert $variant="error">
          Non hai i permessi necessari per accedere a questa pagina.
        </Alert>
      </PageContainerWide>
    );
  }

  const getStatoBadgeVariant = (stato: string | undefined): 'success' | 'error' | 'warning' => {
    if (stato === 'inviata') return 'success';
    if (stato === 'errore' || stato === 'fallita') return 'error';
    return 'warning';
  };

  return (
    <PageContainerWide>
      <PageHeader
        title="Storico Email"
        icon={<Mail />}
        actions={
          <Controls>
            <SearchInput
              type="text"
              placeholder="Cerca per email, oggetto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tutti gli stati</option>
              <option value="in coda">In Coda</option>
              <option value="inviata">Inviata</option>
              <option value="errore">Errore</option>
              <option value="fallita">Fallita</option>
            </Select>
          </Controls>
        }
      />

      {alert.show && (
        <Alert $variant={alert.type}>
          {alert.message}
        </Alert>
      )}

      <StatGrid>
        <StatCard value={stats.total} label="Totale email" />
        <StatCard value={stats.inviata} label="Email inviate" />
        <StatCard value={stats.errore} label="Email con errori" />
        <StatCard value={stats.in_coda} label="In coda" />
      </StatGrid>

      <Card>
        <CardBody>
          <GelatoDataGrid
            rowData={filteredEmails}
            columnDefs={columnDefs}
            loading={loading}
          />
        </CardBody>
      </Card>

      {/* Modale dettagli email */}
      <Modal
        open={showDetailsModal && !!selectedEmail}
        onClose={handleCloseDetails}
        title={selectedEmail ? `Dettagli Email #${selectedEmail.id}` : ''}
        maxWidth="600px"
        footer={
          <Button $variant="secondary" onClick={handleCloseDetails}>
            Chiudi
          </Button>
        }
      >
        {selectedEmail && (
          <>
            <DetailRow>
              <strong>Stato:</strong>{' '}
              <Badge $variant={getStatoBadgeVariant(selectedEmail.stato)}>
                {selectedEmail.stato}
              </Badge>
            </DetailRow>

            <DetailRow>
              <strong>Destinatario:</strong> {selectedEmail.destinatario}
            </DetailRow>

            <DetailRow>
              <strong>Oggetto:</strong> {selectedEmail.oggetto}
            </DetailRow>

            <DetailRow>
              <strong>Data Creazione:</strong> {formatDate(selectedEmail.created_at)}
            </DetailRow>

            <DetailRow>
              <strong>Data Invio:</strong> {formatDate(selectedEmail.sent_at)}
            </DetailRow>

            {selectedEmail.errore && (
              <DetailRow>
                <strong>Errore:</strong>
                <div style={{
                  backgroundColor: 'var(--error-soft)',
                  border: '1px solid var(--error)',
                  color: 'var(--error)',
                  padding: 'var(--space-sm)',
                  borderRadius: 'var(--border-radius-sm)',
                  marginTop: 'var(--space-xs)',
                  fontSize: 'var(--font-size-sm)'
                }}>
                  {selectedEmail.errore}
                </div>
              </DetailRow>
            )}

            {selectedEmail.corpo && (
              <DetailRow>
                <strong>Contenuto Email:</strong>
                <div
                  className="email-html-content"
                  style={{
                    backgroundColor: 'var(--gray-50)',
                    border: '1px solid var(--gray-200)',
                    padding: 'var(--space-md)',
                    borderRadius: 'var(--border-radius-sm)',
                    marginTop: 'var(--space-xs)',
                    maxHeight: '300px',
                    overflow: 'auto',
                    fontSize: 'var(--font-size-sm)',
                    lineHeight: '1.5'
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedEmail.corpo }}
                />
              </DetailRow>
            )}
          </>
        )}
      </Modal>
    </PageContainerWide>
  );
};

export default StoricoEmailPage;
